import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LockSimple, CheckCircle, Sparkle } from "@phosphor-icons/react";

const colorMap = {
  foundation: {
    color: "#5e50f7",
    glow: "0 8px 36px rgba(94,80,247,0.22)",
    tint: "rgba(94,80,247,0.08)",
    iconBg: "rgba(94,80,247,0.18)",
    iconStroke: "#8a7cff",
    eyebrow: "#5e50f7",
    cta: "#8a7cff",
    draftBorder: "rgba(94,80,247,0.40)",
    draftBg: "rgba(94,80,247,0.08)",
    draftText: "#8a7cff",
    ghost: "rgba(94,80,247,0.06)",
    savedBg:
      "linear-gradient(140deg, rgba(94,80,247,0.22) 0%, rgba(94,80,247,0.10) 60%, rgba(31,31,43,1) 100%)",
    savedBorder: "rgba(94,80,247,0.55)",
  },
  attraction: {
    color: "#75dff5",
    glow: "0 8px 36px rgba(117,223,245,0.20)",
    tint: "rgba(117,223,245,0.06)",
    iconBg: "rgba(117,223,245,0.15)",
    iconStroke: "#75dff5",
    eyebrow: "#75dff5",
    cta: "#75dff5",
    draftBorder: "rgba(117,223,245,0.40)",
    draftBg: "rgba(117,223,245,0.08)",
    draftText: "#75dff5",
    ghost: "rgba(117,223,245,0.06)",
    savedBg:
      "linear-gradient(140deg, rgba(117,223,245,0.20) 0%, rgba(117,223,245,0.08) 60%, rgba(31,31,43,1) 100%)",
    savedBorder: "rgba(117,223,245,0.5)",
    activeBg: "rgba(117,223,245,0.10)",
    activeBorder: "rgba(117,223,245,0.50)",
    activeIconBg: "rgba(117,223,245,0.15)",
    activeIconStroke: "#75dff5",
    activeEyebrow: "#75dff5",
  },
  conversion: {
    color: "#ffdb5c",
    glow: "0 8px 36px rgba(255,219,92,0.20)",
    tint: "rgba(255,219,92,0.06)",
    iconBg: "rgba(255,219,92,0.15)",
    iconStroke: "#ffdb5c",
    eyebrow: "#ffdb5c",
    cta: "#ffdb5c",
    draftBorder: "rgba(255,219,92,0.40)",
    draftBg: "rgba(255,219,92,0.08)",
    draftText: "#ffdb5c",
    ghost: "rgba(255,219,92,0.06)",
    savedBg:
      "linear-gradient(140deg, rgba(255,219,92,0.20) 0%, rgba(255,219,92,0.08) 60%, rgba(31,31,43,1) 100%)",
    savedBorder: "rgba(255,219,92,0.5)",
    activeBg: "rgba(255,219,92,0.10)",
    activeBorder: "rgba(255,219,92,0.45)",
    activeIconBg: "rgba(255,219,92,0.15)",
    activeIconStroke: "#ffdb5c",
    activeEyebrow: "#ffdb5c",
  },
};

// Padlock icon per spec (SVG, stroke-only, no Phosphor swap so the visual
// matches the user's description exactly).
function PadlockIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6a6a80"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

export default function AgentCard({
  layer = "foundation",
  title,
  subtitle,
  description,
  icon: Icon,
  to,
  status, // 'ready' | 'generated' | 'saved' | 'coming-soon' | 'locked-prereq' | 'admin-only-tease'
  testid,
  seq,         // optional 1/2/3 — renders ghosted number behind content
  lockReason,  // optional tooltip text shown on hover when locked-prereq
  onLockedClick, // optional callback when a locked card is clicked
}) {
  const c = colorMap[layer];
  const comingSoon = status === "coming-soon";
  const prereqLocked = status === "locked-prereq";
  const adminOnlyTease = status === "admin-only-tease";
  // 'admin-only-tease' = blocks navigation + shows "Hamarosan" pill,
  // BUT keeps the card's full layer color (no grey/desaturate).
  const locked = comingSoon || prereqLocked || adminOnlyTease;
  const greyLocked = comingSoon || prereqLocked; // these grey out the card
  const saved = status === "saved";
  const generated = status === "generated";
  const [showTip, setShowTip] = useState(false);

  const Wrapper = locked ? "div" : Link;
  const wrapperProps = locked
    ? {
        "aria-disabled": true,
        onClick: () => { if (adminOnlyTease && onLockedClick) onLockedClick(); },
      }
    : { to };

  // Only two states exist: ACTIVE (layer-colored) or LOCKED (black).
  // Saved/DONE is just a pill on top of an ACTIVE card - the underlying
  // style does NOT change. Active styling derives from the per-layer
  // colorMap so Vonzás/Értékesítés cards use their own brand-tints
  // (purple for Foundation+Vonzás, gold for Értékesítés).
  const activeBg = c.activeBg || "rgba(94,80,247,0.10)";
  const activeBorder = c.activeBorder || "rgba(94,80,247,0.50)";
  const activeIconBg = c.activeIconBg || "rgba(94,80,247,0.18)";
  const activeIconStroke = c.activeIconStroke || "#8a7cff";
  const activeEyebrow = c.activeEyebrow || "#5e50f7";
  const activeTitle = "#e8e8f0";
  const activeDesc = "#9898ad";

  // Locked cards mirror Attraction HAMAROSAN visual - neutral dark.
  const lockedBg = "rgba(255,255,255,0.02)";
  const lockedBorder = "rgba(255,255,255,0.06)";
  const lockedIconBg = "rgba(255,255,255,0.04)";
  const lockedIconStroke = "#6a6a80";
  const lockedEyebrow = "#6a6a80";
  const lockedTitle = "#9898ad";
  const lockedDesc = "#6a6a80";

  // Ghost number color — uses the layer's ghost tint when active so each
  // layer keeps its own brand color (purple for Foundation+Vonzás, gold
  // for Értékesítés).
  const ghostColor = locked
    ? "rgba(255,255,255,0.025)"
    : (c.ghost || "rgba(94,80,247,0.06)");

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={testid}
      onMouseEnter={(e) => {
        if (prereqLocked) setShowTip(true);
        if (!locked) e.currentTarget.style.boxShadow = c.glow;
      }}
      onMouseLeave={(e) => {
        if (prereqLocked) setShowTip(false);
        if (!locked) e.currentTarget.style.boxShadow = "";
      }}
      className={`group relative flex flex-col h-[220px] rounded-2xl p-6 border transition-all duration-300 overflow-hidden ${
        locked ? "cursor-not-allowed" : "hover:-translate-y-1"
      }`}
      style={{
        background: greyLocked ? lockedBg : activeBg,
        borderColor: greyLocked ? lockedBorder : activeBorder,
      }}
    >
      {/* Ghosted sequence number (Foundation only) - sits behind content */}
      {typeof seq === "number" && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            right: -4,
            bottom: -54,
            fontFamily: "inherit",
            fontWeight: 600,
            fontSize: 200,
            lineHeight: 1,
            letterSpacing: "-0.04em",
            color: ghostColor,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {seq}
        </div>
      )}

      {/* corner glow */}
      {!locked && (
        <div
          className="absolute -top-16 -right-16 w-40 h-40 rounded-full pointer-events-none opacity-40 blur-3xl"
          style={{ background: c.tint, zIndex: 0 }}
        />
      )}
      {saved && (
        <Sparkle
          size={14}
          weight="fill"
          color={c.color}
          className="absolute top-3 right-3 mex-sparkle-idle"
          style={{ zIndex: 2 }}
        />
      )}

      {/* Content layer (above ghost number) */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: greyLocked ? lockedIconBg : activeIconBg,
              border: greyLocked ? "1px solid rgba(255,255,255,0.06)" : "none",
            }}
          >
            {Icon && (
              <Icon
                size={22}
                weight="duotone"
                color={greyLocked ? lockedIconStroke : activeIconStroke}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {saved && (
              <span
                data-testid={`${testid}-saved-badge`}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full font-medium"
                style={{
                  background: "rgba(16, 185, 129, 0.14)",
                  border: "1px solid rgba(16, 185, 129, 0.35)",
                  color: "#10b981",
                }}
              >
                <CheckCircle size={10} weight="fill" /> DONE
              </span>
            )}
            {generated && (
              <span
                className="text-[10px] px-2 py-1 rounded-full border"
                style={{
                  borderColor: "rgba(94,80,247,0.40)",
                  color: "#8a7cff",
                  background: "rgba(94,80,247,0.08)",
                }}
              >
                DRAFT
              </span>
            )}
            {comingSoon && (
              <span className="text-[10px] px-2 py-1 rounded-full border border-white/10 text-[#a1a1a1] flex items-center gap-1">
                <LockSimple size={10} /> HAMAROSAN
              </span>
            )}
            {adminOnlyTease && (
              <span
                data-testid={`${testid}-coming-pill`}
                className="text-[10px] px-2.5 py-1 rounded-full font-semibold tracking-[0.08em]"
                style={{
                  background: "rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.78)",
                  backdropFilter: "blur(6px)",
                }}
              >
                HAMAROSAN
              </span>
            )}
            {prereqLocked && (
              <span
                data-testid={`${testid}-lock-pill`}
                className="inline-flex items-center justify-center text-[10px] px-2 py-1 rounded-full border"
                style={{
                  background: "transparent",
                  borderColor: "rgba(255,255,255,0.12)",
                  color: "#6a6a80",
                  minWidth: 56,
                  minHeight: 25,
                }}
                aria-label="Zárolt"
              >
                <PadlockIcon />
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {subtitle && (
            <div
              className="text-[10px] tracking-[0.22em] uppercase mb-1"
              style={{ color: greyLocked ? lockedEyebrow : activeEyebrow }}
            >
              {subtitle}
            </div>
          )}
          <h3
            className="font-heading text-[19px] mb-1.5 leading-tight"
            style={{ color: greyLocked ? lockedTitle : activeTitle }}
          >
            {title}
          </h3>
          <p
            className="text-[13px] leading-relaxed line-clamp-2"
            style={{ color: greyLocked ? lockedDesc : activeDesc }}
          >
            {description}
          </p>
        </div>
      </div>

      {/* Hover tooltip (prereq-locked cards only) */}
      {prereqLocked && lockReason && showTip && (
        <div
          role="tooltip"
          data-testid={`${testid}-lock-tooltip`}
          style={{
            position: "absolute",
            top: -40,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1a1a28",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 12,
            color: "#d4d4e0",
            whiteSpace: "nowrap",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          {lockReason}
          <div
            style={{
              position: "absolute",
              bottom: -5,
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 8,
              height: 8,
              background: "#1a1a28",
              borderRight: "1px solid rgba(255,255,255,0.10)",
              borderBottom: "1px solid rgba(255,255,255,0.10)",
            }}
          />
        </div>
      )}
    </Wrapper>
  );
}
